import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import WrapperComponent from '../commonComponents/WrapperComponent';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { url } from '../../util/config';

const CreateWhatsappProfileComponent = () => {
    const [mobileNo, setMobileNo] = useState('');
    const [enterpriseName, setEnterpriseName] = useState('');
    const [websiteUrl, setWebsiteUrl] = useState('');
    const [orgAddress, setOrgAddress] = useState('');
    const [shortDescription, setShortDescription] = useState('');
    const [allocatedUser, setAllocatedUser] = useState('');
    const [userOptions, setUserOptions] = useState([]);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchUsers = async () => {
            const token = localStorage.getItem('token');
            try {
                const response = await fetch(`${url}/users`, {
                    method: 'GET',
                    headers: { 'Authorization': 'Bearer ' + token },
                });
                if (!response.ok) throw new Error('Failed to fetch users');
                const data = await response.json();
                setUserOptions(data);
            } catch (error) {
                console.error('Error fetching users:', error);
                toast.error('Failed to load users.');
            }
        };
        fetchUsers();
    }, []);

    const mobileRegex = /^[6-9]\d{9}$/;
    const urlRegex = /^(https?:\/\/)?([\w\d-]+\.)+[\w-]{2,4}(\/.*)?$/;

    const validate = (field, value) => {
        const newErrors = { ...errors };
        switch (field) {
            case 'allocatedUser':
                if (!value) newErrors.allocatedUser = 'Please select a user.';
                else delete newErrors.allocatedUser;
                break;
            case 'mobileNo':
                if (!value) newErrors.mobileNo = 'Mobile number is required.';
                else if (!mobileRegex.test(value)) newErrors.mobileNo = 'Please enter a valid 10-digit Indian mobile number.';
                else delete newErrors.mobileNo;
                break;
            case 'enterpriseName':
                if (!value) newErrors.enterpriseName = 'Enterprise name is required.';
                else delete newErrors.enterpriseName;
                break;
            case 'websiteUrl':
                if (!value) newErrors.websiteUrl = 'Website URL is required.';
                else if (!urlRegex.test(value)) newErrors.websiteUrl = 'Please enter a valid URL.';
                else delete newErrors.websiteUrl;
                break;
            case 'orgAddress':
                if (!value) newErrors.orgAddress = 'Organisation address is required.';
                else delete newErrors.orgAddress;
                break;
            case 'shortDescription':
                if (value.length > 60) newErrors.shortDescription = 'Short description cannot exceed 60 characters.';
                else delete newErrors.shortDescription;
                break;
            default: break;
        }
        setErrors(newErrors);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const submitErrors = {};
        if (!allocatedUser) submitErrors.allocatedUser = 'Please select a user.';
        if (!mobileNo) submitErrors.mobileNo = 'Mobile number is required.';
        else if (!mobileRegex.test(mobileNo)) submitErrors.mobileNo = 'Please enter a valid 10-digit Indian mobile number.';
        if (!enterpriseName) submitErrors.enterpriseName = 'Enterprise name is required.';
        if (!websiteUrl) submitErrors.websiteUrl = 'Website URL is required.';
        else if (!urlRegex.test(websiteUrl)) submitErrors.websiteUrl = 'Please enter a valid URL.';
        if (!orgAddress) submitErrors.orgAddress = 'Organisation address is required.';
        if (shortDescription.length > 60) submitErrors.shortDescription = 'Short description cannot exceed 60 characters.';

        if (Object.keys(submitErrors).length > 0) {
            setErrors(submitErrors);
            return;
        }

        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${url}/profiles`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    whatsapp_no: mobileNo,
                    enterprise_name: enterpriseName,
                    website_url: websiteUrl,
                    org_address: orgAddress,
                    short_desc: shortDescription,
                    country: '91',
                    owner: allocatedUser,
                }),
            });

            if (response.ok) {
                toast.success('Profile created successfully!', { autoClose: 3000 });
                setMobileNo(''); setEnterpriseName(''); setWebsiteUrl('');
                setOrgAddress(''); setShortDescription(''); setAllocatedUser('');
                setErrors({});
                setTimeout(() => navigate('/whatsappprofile'), 3000);
            } else {
                const errorData = await response.json();
                toast.error(errorData.error || errorData.message || 'Failed to create profile. Please try again.', { autoClose: 3000 });
            }
        } catch (error) {
            console.error('Error creating profile:', error);
            toast.error('An error occurred. Please try again.', { autoClose: 3000 });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <WrapperComponent>
            <div style={styles.page}>
                <div style={styles.card}>
                    <div style={styles.cardHeader}>
                        <div style={styles.iconCircle}>
                            <i className="bi bi-whatsapp" style={{ fontSize: '20px', color: '#1565c0' }}></i>
                        </div>
                        <div>
                            <h5 style={styles.cardTitle}>Create WhatsApp Profile</h5>
                            <p style={styles.cardSubtitle}>Add a new WhatsApp business profile</p>
                        </div>
                    </div>

                    <div style={styles.cardBody}>
                        <form onSubmit={handleSubmit} noValidate>
                            {/* Allocated User */}
                            <div style={styles.fieldGroup}>
                                <label style={styles.label}>Allocated To</label>
                                <select
                                    value={allocatedUser}
                                    style={{ ...styles.input, ...(errors.allocatedUser ? styles.inputError : {}) }}
                                    onChange={(e) => setAllocatedUser(e.target.value)}
                                    onBlur={() => validate('allocatedUser', allocatedUser)}
                                >
                                    <option value="">Select User</option>
                                    {userOptions.map((user) => (
                                        <option key={user._id} value={user._id}>{user.name}</option>
                                    ))}
                                </select>
                                {errors.allocatedUser && <span style={styles.fieldError}>{errors.allocatedUser}</span>}
                            </div>

                            {/* Mobile No */}
                            <div style={styles.fieldGroup}>
                                <label style={styles.label}>WhatsApp Mobile Number</label>
                                <input
                                    type="text"
                                    value={mobileNo}
                                    placeholder="10-digit Indian mobile number"
                                    maxLength="10"
                                    style={{ ...styles.input, ...(errors.mobileNo ? styles.inputError : {}) }}
                                    onChange={(e) => setMobileNo(e.target.value)}
                                    onBlur={() => validate('mobileNo', mobileNo)}
                                />
                                {errors.mobileNo && <span style={styles.fieldError}>{errors.mobileNo}</span>}
                            </div>

                            {/* Enterprise Name */}
                            <div style={styles.fieldGroup}>
                                <label style={styles.label}>Enterprise Name</label>
                                <input
                                    type="text"
                                    value={enterpriseName}
                                    placeholder="Enter enterprise name"
                                    maxLength="100"
                                    style={{ ...styles.input, ...(errors.enterpriseName ? styles.inputError : {}) }}
                                    onChange={(e) => setEnterpriseName(e.target.value)}
                                    onBlur={() => validate('enterpriseName', enterpriseName)}
                                />
                                {errors.enterpriseName && <span style={styles.fieldError}>{errors.enterpriseName}</span>}
                            </div>

                            {/* Website URL */}
                            <div style={styles.fieldGroup}>
                                <label style={styles.label}>Website URL</label>
                                <input
                                    type="text"
                                    value={websiteUrl}
                                    placeholder="https://yourwebsite.com"
                                    style={{ ...styles.input, ...(errors.websiteUrl ? styles.inputError : {}) }}
                                    onChange={(e) => setWebsiteUrl(e.target.value)}
                                    onBlur={() => validate('websiteUrl', websiteUrl)}
                                />
                                {errors.websiteUrl && <span style={styles.fieldError}>{errors.websiteUrl}</span>}
                            </div>

                            {/* Organisation Address */}
                            <div style={styles.fieldGroup}>
                                <label style={styles.label}>Organisation Address</label>
                                <textarea
                                    value={orgAddress}
                                    placeholder="Enter full organisation address"
                                    rows="3"
                                    maxLength="500"
                                    style={{ ...styles.input, resize: 'vertical', ...(errors.orgAddress ? styles.inputError : {}) }}
                                    onChange={(e) => setOrgAddress(e.target.value)}
                                    onBlur={() => validate('orgAddress', orgAddress)}
                                />
                                {errors.orgAddress && <span style={styles.fieldError}>{errors.orgAddress}</span>}
                            </div>

                            {/* Short Description */}
                            <div style={styles.fieldGroup}>
                                <label style={styles.label}>
                                    Short Description
                                    <span style={styles.charCount}>{shortDescription.length}/60</span>
                                </label>
                                <input
                                    type="text"
                                    value={shortDescription}
                                    placeholder="Brief description (max 60 characters)"
                                    maxLength="60"
                                    style={{ ...styles.input, ...(errors.shortDescription ? styles.inputError : {}) }}
                                    onChange={(e) => setShortDescription(e.target.value)}
                                    onBlur={() => validate('shortDescription', shortDescription)}
                                />
                                {errors.shortDescription && <span style={styles.fieldError}>{errors.shortDescription}</span>}
                            </div>

                            <div style={styles.actions}>
                                <button type="button" style={styles.btnSecondary} onClick={() => navigate('/whatsappprofile')}>
                                    Cancel
                                </button>
                                <button type="submit" style={styles.btnPrimary} disabled={isLoading}>
                                    {isLoading
                                        ? <><span style={styles.spinner}></span> Creating…</>
                                        : 'Create Profile'}
                                </button>
                            </div>
                        </form>
                        <ToastContainer />
                    </div>
                </div>
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </WrapperComponent>
    );
};

const styles = {
    page: { display: 'flex', justifyContent: 'center', paddingTop: '8px', fontFamily: "'Segoe UI', system-ui, sans-serif" },
    card: { background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', width: '100%', maxWidth: '560px', border: '1px solid #e5e7eb', overflow: 'hidden' },
    cardHeader: { display: 'flex', alignItems: 'center', gap: '14px', padding: '20px 24px', borderBottom: '1px solid #f3f4f6', background: '#f8fafc' },
    iconCircle: { width: '44px', height: '44px', borderRadius: '10px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    cardTitle: { margin: 0, fontSize: '16px', fontWeight: '700', color: '#111827' },
    cardSubtitle: { margin: 0, fontSize: '13px', color: '#6b7280' },
    cardBody: { padding: '24px' },
    fieldGroup: { marginBottom: '18px', display: 'flex', flexDirection: 'column', gap: '5px' },
    label: { fontSize: '13px', fontWeight: '600', color: '#374151', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    charCount: { fontSize: '12px', color: '#9ca3af', fontWeight: '400' },
    input: { padding: '10px 12px', fontSize: '14px', border: '1.5px solid #d1d5db', borderRadius: '8px', outline: 'none', width: '100%', boxSizing: 'border-box', color: '#111827', fontFamily: 'inherit' },
    inputError: { borderColor: '#ef4444' },
    fieldError: { fontSize: '12px', color: '#ef4444' },
    actions: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' },
    btnPrimary: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: 'linear-gradient(to right, #2388e0, #1565c0)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
    btnSecondary: { padding: '10px 20px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
    spinner: { width: '15px', height: '15px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' },
};

export default CreateWhatsappProfileComponent;
